import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Drawer,
  InputAdornment
} from '@mui/material';
import {
  ChevronRight,
  ChevronLeft,
  Close,
  HelpOutline,
} from '@mui/icons-material';
import { API_URL } from '../../../utils/apiConfig';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

const MIN_DRAWER_WIDTH = 400;
const MAX_DRAWER_WIDTH = 1200;
const DEFAULT_DRAWER_WIDTH = 600;

const PDFViewer = ({ pdfUrl, height = '80vh' }) => {
  console.log('PDFViewer rendering with URL:', pdfUrl);
  
  if (!pdfUrl) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'background.paper', borderRadius: 1, mb: 3 }}>
        <Typography color="text.secondary">Loading PDF...</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ height, backgroundColor: 'background.paper', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
      <iframe
        src={pdfUrl}
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="W-4 Form"
      />
    </Box>
  );
};

const W4Form = ({ 
  employeeId, 
  formId,
  employee,
  isCompleted: initialIsCompleted,
  onSubmit,
  filePath
}) => {
  // Debug logging for props
  console.log('W4Form - Props received:', {
    employeeId,
    formId,
    isCompleted: initialIsCompleted,
    filePath,
    hasFilePath: !!filePath
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [signatureDate, setSignatureDate] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(!initialIsCompleted);

  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const iframeRef = useRef(null);
  const resizeRef = useRef(null);

  // Form Data State - all fields that match the W4 form
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    firstName: employee?.firstName || '',
    middleInitial: '',
    lastName: employee?.lastName || '',
    ssn: '',
    address: '',
    cityStateZip: '',
    filingStatus: 'single', // single, married, or head

    // Step 2: Multiple Jobs
    multipleJobsCheckbox: false,
    
    // Multiple Jobs Worksheet fields
    higherJobWage: '',
    lowerJobWage: '',
    line1Amount: '',
    line2aAmount: '',
    line2bAmount: '',
    line2cAmount: '',
    line3PayPeriods: '52', // Default to weekly
    line4ExtraWithholding: '',

    // Step 3: Dependents
    qualifyingChildren: '',
    otherDependents: '',
    totalDependents: '',

    // Step 4: Other Adjustments
    otherIncome: '',       // 4(a)
    deductions: '',        // 4(b)
    extraWithholding: '',  // 4(c)

    // Deductions Worksheet fields
    deductionsLine1: '',
    deductionsLine2: '',
    deductionsLine3: '',
    deductionsLine4: '',
    deductionsLine5: '',

    // First Date of Employment
    firstDateEmployment: '',
    
    // For claiming exemption from withholding
    claimExemption: false
  });

  const [employerInfo, setEmployerInfo] = useState({
    name: '',
    address: '',
    phone: '',
    EIN: ''
  });

  // Form Validation State
  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    ssn: false,
    address: false,
    cityStateZip: false,
    signature: false
  });

  // Drawer controls
  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const newWidth = document.body.clientWidth - e.clientX;
    if (newWidth >= MIN_DRAWER_WIDTH && newWidth <= MAX_DRAWER_WIDTH) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    setIsResizing(false);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    setIsResizing(true);
    e.preventDefault();
  }, [handleMouseMove, handleMouseUp]);

  const getDrawerWidth = () => {
    if (isMobile) return '100%';
    return `${drawerWidth}px`;
  };

  const togglePreview = () => {
    setIsPreviewOpen(!isPreviewOpen);
  };

  const handleSignatureDateChange = (e) => {
    setSignatureDate(e.target.value);
  };

  // Clean up event listeners
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Fetch employer info from W4 endpoint
  useEffect(() => {
    const fetchEmployerInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        // Use the W4 data endpoint that returns JSON data
        const response = await fetch(`${API_URL}/forms/w4/data/${employeeId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch W4 data');
        }
        const data = await response.json();
        
        // Set employer info from the response
        if (data.employer) {
          setEmployerInfo({
            name: data.employer.name,
            address: data.employer.address,
            phone: data.employer.phone,
            EIN: data.employer.EIN || ''
          });
        } else {
          throw new Error('Employer information not found');
        }
      } catch (error) {
        console.error('Error fetching employer info:', error);
        setError('Failed to load employer information');
      }
    };

    if (employeeId) {
      fetchEmployerInfo();
    }
  }, [employeeId]);

  // Fetch PDF using API endpoint (like wage rate notice)
  useEffect(() => {
    const fetchPDF = async () => {
      // Always use API endpoint for completed forms, ignore filePath prop
      if (!isCompleted) {
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        
        // Always use the API endpoint - don't use filePath directly
        const response = await fetch(`${API_URL}/forms/w4/${employeeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('W4 PDF Fetch Error:', errorText);
          throw new Error(`Failed to fetch W4 PDF: ${response.status} ${errorText}`);
        }

        const pdfBlob = await response.blob();
        const url = URL.createObjectURL(pdfBlob);
        console.log('W4 PDF loaded successfully, blob URL:', url);
        setPdfUrl(url);
      } catch (err) {
        console.error('Error fetching W4 PDF:', err);
        setError(err.message || 'Failed to load W4 form');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPDF();

    // Cleanup
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [employeeId, initialIsCompleted]);

  // Fetch hire date
  useEffect(() => {
    const fetchHireDate = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/employees`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch employee data');
        }
        
        const employees = await response.json();
        const employeeData = employees.find(emp => emp.id === Number(employeeId));
        
        if (employeeData?.hireDate) {
          // Parse the ISO date string and format it as YYYY-MM-DD
          const date = new Date(employeeData.hireDate);
          const formattedDate = date.toISOString().split('T')[0];
          
          setFormData(prev => ({
            ...prev,
            firstDateEmployment: formattedDate
          }));
        }
      } catch (error) {
        console.error('Error fetching hire date:', error);
        setError('Failed to load hire date');
      }
    };

    if (employeeId) {
      fetchHireDate();
    }
  }, [employeeId]);

  // Initialize signature pad
  useEffect(() => {
    let mounted = true;

    const initializeSignaturePad = async () => {
      if (!isCompleted && canvasRef.current && !signaturePadRef.current) {
        try {
          const SignaturePad = (await import('signature_pad')).default;

          // Re-check after the await. signature_pad is a lazily-imported chunk,
          // so on a cold cache this gap is long enough for a re-render to null
          // the ref — reading it blind threw "Cannot read properties of null
          // (reading 'parentElement')", the pad never initialised, and the
          // employee got "Please provide your signature" on a pad they had
          // already drawn on.
          if (!mounted || !canvasRef.current) return;

          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const canvas = canvasRef.current;
          const container = canvas.parentElement;
          if (!container) return;
          const containerWidth = container.offsetWidth;
          
          canvas.style.width = '100%';
          canvas.style.height = '150px';
          canvas.width = containerWidth * ratio;
          canvas.height = 150 * ratio;
          
          const context = canvas.getContext('2d');
          context.scale(ratio, ratio);
          
          if (mounted) {
            signaturePadRef.current = new SignaturePad(canvas, {
              backgroundColor: 'rgb(255, 255, 255)',
              penColor: 'rgb(0, 0, 0)',
              velocityFilterWeight: 0.7,
              minWidth: 0.5,
              maxWidth: 2.5,
              throttle: 16,
            });
          }
        } catch (error) {
          console.error('Error initializing signature pad:', error);
        }
      }
    };

    initializeSignaturePad();

    return () => {
      mounted = false;
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
        signaturePadRef.current = null;
      }
    };
  }, [isCompleted]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      ssn: !/^\d{3}-\d{2}-\d{4}$/.test(formData.ssn),
      address: !formData.address.trim(),
      cityStateZip: !formData.cityStateZip.trim(),
      signature: !signaturePadRef.current || signaturePadRef.current.isEmpty()
    };
    
    setFormErrors(errors);
    
    // Show detailed error message
    if (Object.values(errors).some(error => error)) {
      const errorMessages = [];
      if (errors.firstName || errors.lastName) errorMessages.push("Your full name");
      if (errors.ssn) errorMessages.push("A valid SSN (format: XXX-XX-XXXX)");
      if (errors.address || errors.cityStateZip) errorMessages.push("Your complete address");
      if (errors.signature) errorMessages.push("Your signature");
      
      setError(`Please provide: ${errorMessages.join(", ")}`);
      return false;
    }
    
    return true;
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setIsCompleted(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const signatureDataUrl = signaturePadRef.current.toDataURL('image/png');

      const response = await fetch(`${API_URL}/forms/w4/fill-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          employeeId,
          formId,
          formData: {
            ...formData,
            employerInfo
          },
          signatureImage: signatureDataUrl,
          signatureDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit W4 form');
      }

      const result = await response.json();
      
      // Update states immediately
      setIsCompleted(true);
      setPdfUrl(result.pdfPath);
      setIsEditing(false);
      
      // Call onSubmit with both formId and filePath
      if (onSubmit) {
        await onSubmit(formId, result.pdfPath);
      }
    } catch (err) {
      console.error('Error submitting W4 form:', err);
      setError(err.message || 'Failed to submit W4 form');
    } finally {
      setIsLoading(false);
    }
  };

  if (isCompleted && !isEditing) {
    if (!filePath && !pdfUrl) {
      return (
        <Box sx={{ width: '100%', textAlign: 'center', py: 3 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="textSecondary">
            Loading completed form...
          </Typography>
        </Box>
      );
    }

    // Use the blob URL from the API (pdfUrl) - ignore the filePath prop
    const authenticatedUrl = pdfUrl;  // Pass null or blob URL directly
    
    console.log('W4Form render - PDF state:', { 
      pdfUrl, 
      authenticatedUrl, 
      isCompleted, 
      isLoading, 
      error 
    });

    return (
      <Box sx={{ width: '100%' }}>
        <Paper 
          elevation={1}
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            borderRadius: 1
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e0e0e0',
              bgcolor: 'primary.light',
            }}
          >
            <Typography variant="h6" sx={{ color: 'black' }}>
              W-4 Form
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartEdit}
                sx={{ mr: 1 }}
              >
                Fill Form Again
              </Button>
            </Box>
          </Box>

          <PDFViewer pdfUrl={authenticatedUrl} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Box sx={{ 
        flex: 1, 
        maxWidth: isPreviewOpen ? `calc(100% - ${drawerWidth}px)` : '100%', 
        transition: isResizing ? 'none' : 'max-width 0.3s ease',
        pr: isPreviewOpen ? `${drawerWidth}px` : 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ flex: 1 }}>
            Form W-4 (2025)
          </Typography>
          
          <IconButton 
            onClick={togglePreview}
            sx={{ 
              position: 'fixed',
              right: isPreviewOpen ? `${drawerWidth}px` : 0,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 1,
              zIndex: 1000,
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
          >
            {isPreviewOpen ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff5b3', borderRadius: 1, textAlign: 'left', mb:1, border: '2px solid rgb(231, 164, 164)' }}>
          <Typography variant="body1">
            <a href="https://www.tax.ny.gov/forms/current-forms/it/it2104i.htm" target="_blank" rel="noopener noreferrer">Complete Form W-4 so that your employer can withhold the correct federal income tax from your pay.</a> Consider completing a new Form W-4 each year and when your personal or financial situation changes.
          </Typography>
        </Box>     
      
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* Personal Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 1: Enter Personal Information
                <Tooltip title="Enter your personal information exactly as it appears on your social security card">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    error={formErrors.firstName}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Middle Initial"
                    name="middleInitial"
                    value={formData.middleInitial}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    error={formErrors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Social Security Number"
                    name="ssn"
                    value={formData.ssn}
                    onChange={handleInputChange}
                    required
                    error={formErrors.ssn}
                    helperText="Format: XXX-XX-XXXX"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    error={formErrors.address}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="City, State, and ZIP Code"
                    name="cityStateZip"
                    value={formData.cityStateZip}
                    onChange={handleInputChange}
                    required
                    error={formErrors.cityStateZip}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle1" gutterBottom>
                      Filing Status
                      <Tooltip title="Select your tax filing status. This affects your standard deduction and tax brackets">
                        <IconButton size="small">
                          <HelpOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    <RadioGroup
                      name="filingStatus"
                      value={formData.filingStatus}
                      onChange={handleInputChange}
                    >
                      <FormControlLabel
                        value="single"
                        control={<Radio />}
                        label="Single or Married filing separately"
                      />
                      <FormControlLabel
                        value="married"
                        control={<Radio />}
                        label="Married filing jointly"
                      />
                      <FormControlLabel
                        value="head"
                        control={<Radio />}
                        label="Head of household"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Multiple Jobs Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 2: Multiple Jobs or Spouse Works
                <Tooltip title="Complete this step if you hold more than one job or are married filing jointly and your spouse also works">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                Complete this step if you (1) hold more than one job at a time, or (2) are married filing jointly and your spouse also works. 
                The correct amount of withholding depends on income earned from all of these jobs.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                Do only one of the following:
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                (a) Use the estimator at www.irs.gov/W4App for the most accurate withholding for this step (and Steps 3–4). If you or your spouse have self-employment income, use this option; or
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                (b) Use the Multiple Jobs Worksheet on page 3 and enter the result in Step 4(c) below; or
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, pl: 2 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  (c) If there are only two jobs total, you may check this box. Do the same on Form W-4 for the other job:
                </Typography>
                <Checkbox
                  checked={formData.multipleJobsCheckbox}
                  onChange={handleInputChange}
                  name="multipleJobsCheckbox"
                />
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, pl: 4 }}>
                This option is generally more accurate than (b) if pay at the lower paying job is more than half of the pay at the higher paying job. Otherwise, (b) is more accurate.
              </Typography>
              
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Complete Steps 3–4(b) on Form W-4 for only ONE of these jobs. Leave those steps blank for the other jobs. (Your withholding will be most accurate if you complete Steps 3–4(b) on the Form W-4 for the highest paying job.)
              </Typography>
              
              {formData.multipleJobsCheckbox && (
                <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    For Two Jobs Only - Complete if you checked the box above
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Higher Paying Job Annual Wage"
                        name="higherJobWage"
                        value={formData.higherJobWage}
                        onChange={handleInputChange}
                        InputProps={{ 
                          inputProps: { min: 0 },
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Lower Paying Job Annual Wage"
                        name="lowerJobWage"
                        value={formData.lowerJobWage}
                        onChange={handleInputChange}
                        InputProps={{ 
                          inputProps: { min: 0 },
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Multiple Jobs Worksheet (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        1. Two jobs. Enter amount from table on page 4 for higher and lower paying job combination:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line1Amount"
                        value={formData.line1Amount}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        2a. Three jobs. Enter amount from table for highest and second highest paying jobs:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line2aAmount"
                        value={formData.line2aAmount}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        2b. Three jobs. Enter amount from table for highest and third highest paying jobs:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line2bAmount"
                        value={formData.line2bAmount}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        2c. Add the amounts from lines 2a and 2b:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line2cAmount"
                        value={formData.line2cAmount}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        3. Enter number of pay periods per year for the highest paying job:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line3PayPeriods"
                        value={formData.line3PayPeriods}
                        onChange={handleInputChange}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        4. Divide line 1 or line 2c by line 3. Enter result here and in Step 4(c):
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="line4ExtraWithholding"
                        value={formData.line4ExtraWithholding}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Dependents Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 3: Claim Dependents
                <Tooltip title="Enter direct dollar amounts for qualifying children and other dependents">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                If your total income will be $200,000 or less ($400,000 or less if married filing jointly):
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      Multiply the number of qualifying children under age 17 by $2,000:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="qualifyingChildren"
                      value={formData.qualifyingChildren}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      Multiply the number of other dependents by $500:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="otherDependents"
                      value={formData.otherDependents}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      Add the amounts above for qualifying children and other dependents. You may add to this the amount of any other credits. Enter the total here:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="totalDependents"
                      value={formData.totalDependents}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Other Adjustments Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 4: Other Adjustments (Optional)
                <Tooltip title="Enter any additional income, deductions, or extra withholding you want to account for">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      (a) Other income (not from jobs). If you want tax withheld for other income you expect this year that won't have withholding, enter the amount of other income here. This may include interest, dividends, and retirement income:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="otherIncome"
                      value={formData.otherIncome}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      (b) Deductions. If you expect to claim deductions other than the standard deduction and want to reduce your withholding, use the Deductions Worksheet on page 3 and enter the result here:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="deductions"
                      value={formData.deductions}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ minWidth: '60%' }}>
                      (c) Extra withholding. Enter any additional tax you want withheld each pay period:
                    </Typography>
                    <TextField
                      fullWidth
                      type="text"
                      name="extraWithholding"
                      value={formData.extraWithholding}
                      onChange={handleInputChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Deductions Worksheet (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        1. Enter an estimate of your 2025 itemized deductions:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="deductionsLine1"
                        value={formData.deductionsLine1}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ pl: 2, mb: 2 }}>
                      Such deductions may include qualifying home mortgage interest, charitable contributions, state and local taxes (up to $10,000), and medical expenses in excess of 7.5% of your income
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        2. Enter your standard deduction based on filing status:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="deductionsLine2"
                        value={formData.deductionsLine2 }
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ pl: 2, mb: 2 }}>
                      • $30,000 if married filing jointly or qualifying surviving spouse<br />
                      • $22,500 if head of household<br />
                      • $15,000 if single or married filing separately
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        3. If line 1 is greater than line 2, subtract line 2 from line 1 and enter result here. If line 2 is greater than line 1, enter "-0-":
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="deductionsLine3"
                        value={formData.deductionsLine3}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        4. Enter an estimate of your student loan interest, deductible IRA contributions, and certain other adjustments:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="deductionsLine4"
                        value={formData.deductionsLine4}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography sx={{ minWidth: '60%' }}>
                        5. Add lines 3 and 4. Enter the result here and in Step 4(b) above:
                      </Typography>
                      <TextField
                        fullWidth
                        type="text"
                        name="deductionsLine5"
                        value={formData.deductionsLine5}
                        onChange={handleInputChange}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Exemption from Withholding Option */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Exemption from Withholding (Optional)
                <Tooltip title="You may claim exemption from withholding if you meet the requirements">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                You may claim exemption from withholding for 2025 if you meet both of these conditions:
                <ul>
                  <li>You had no federal income tax liability in 2024, and</li>
                  <li>You expect to have no federal income tax liability in 2025</li>
                </ul>
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.claimExemption}
                    onChange={handleInputChange}
                    name="claimExemption"
                  />
                }
                label="I claim exemption from withholding"
              />
            </CardContent>
          </Card>

          {/* Employer Information Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Employer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Employer's Name"
                    value={employerInfo.name}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Employer's Address"
                    value={employerInfo.address}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Employer's Phone"
                    value={employerInfo.phone}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Employer Identification Number (EIN)"
                    value={employerInfo.EIN}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="First Date of Employment"
                    name="firstDateEmployment"
                    value={formData.firstDateEmployment}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Signature Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sign Here
                <Tooltip title="Your signature is required to complete this form">
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{ 
                      bgcolor: 'white',
                      mb: 2
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      style={{
                        width: '100%',
                        height: '150px',
                        touchAction: 'none'
                      }}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    onClick={() => signaturePadRef.current?.clear()}
                    sx={{ mb: 2 }}
                  >
                    Clear Signature
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    name="signatureDate"
                    value={signatureDate}
                    onChange={handleSignatureDateChange}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoading}
            sx={{ mb: 4 }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : (
              'Submit Form'
            )}
          </Button>
        </form>
      </Box>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        anchor={isMobile ? "bottom" : "right"}
        open={isPreviewOpen}
        onClose={togglePreview}
        sx={{
          width: getDrawerWidth(),
          height: isMobile ? '80vh' : '100vh',
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: getDrawerWidth(),
            height: isMobile ? '80vh' : '100vh',
            boxSizing: 'border-box',
            top: isMobile ? 'auto' : 64,
          },
        }}
      >
        {!isMobile && (
          <Box
            ref={resizeRef}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              cursor: 'col-resize',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.2,
              },
              ...(isResizing && {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.2,
                width: '4px',
              }),
            }}
            onMouseDown={handleMouseDown}
          />
        )}

        <Box sx={{ 
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="h6">
              Form Preview
            </Typography>
            {isMobile && (
              <IconButton onClick={togglePreview} edge="end">
                <Close />
              </IconButton>
            )}
          </Box>
          
          <Box 
            sx={{ 
              flexGrow: 1,
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
              height: isMobile ? 'calc(80vh - 80px)' : 'calc(100vh - 200px)',
              userSelect: isResizing ? 'none' : 'auto',
            }}
          >
            <iframe
              ref={iframeRef}
              src="/static-forms/w4-form-2025.pdf"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                pointerEvents: isResizing ? 'none' : 'auto',
              }}
              title="W-4 Form Preview"
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default W4Form;